import { definePreset } from '@primeng/themes';
import type { Preset } from '@primeuix/themes/types';
import { DefaultPreset } from '../default.preset';

export const IndagoNewPreset = definePreset(DefaultPreset, {
  primitive: {
    whiteGrey: {
      10: '#FFFFFF',
      13: '#FCFCFC',
      15: '#F8F8F9',
      20: '#F2F2F2',
      30: '#CBD5E1',
      40: '#CACACA',
      50: '#BFBFBF',
      55: '#9CA3AF',
      60: '#666666',
      70: '#9C99A9',
      80: '#615B6A',
      87: '#37393B',
      85: '#2C2C2C',
      90: '#1A1A1A',
      95: '#302F3B',
      100: '#252B3B',
    },
    green: {
      10: '#F9FFE9',
      15: '#F5F7E5',
      20: '#E5F7E7',
      25: '#61DDAA',
      30: '#059669',
      35: '#2E9C6F',
      40: '#10B981',
      45: '#3F6236',
      50: '#84CC16',
      60: '#183f32',
      80: '#243B1A',
      90: '#303B1A',
      100: '#212915',
    },
    grey: {
      20: '#B5B5B5',
    },
    gray: {
      50: '#FAFAFA',
      100: '#F4F4F7',
      200: '#E8E8EC',
      400: '#9C9C9C',
      500: '#6B6B6B',
      950: '#0A0A0A',
    },
    red: {
      10: '#F9E4E8',
      20: '#F8D5DD',
      30: '#B17B85',
      35: '#EF4444',
      40: '#D10A22',
      45: '#F87575',
      50: '#D10A22',
      60: '#800020',
      70: '#4D2324',
      80: '#34171E',
      90: '#4C0214',
    },
    orange: {
      10: '#F7C7A6',
      15: '#F59E0B',
      20: '#FF8C00',
      25: '#F97316',
      30: '#BD7E00',
      90: '#4C2002',
    },
    yellow: {
      10: '#FFF8E7',
      20: '#FFEBB9',
      30: '#F8CC75',
      45: '#8E680A',
      50: '#FEFCE8',
      80: '#45391A',
      90: '#29251D',
      100: '#29251D',
    },
    blue: {
      2: '#F8FBFF',
      3: '#E8F2FF',
      4: '#EDF5FE',
      6: '#A9BFD8',
      10: '#8AC5FF',
      15: '#5BA2FF',
      20: '#1E9DCA',
      30: '#3F8BD1',
      35: '#65789B',
      40: '#27496F',
      50: '#eff6ff',
      55: '#2E4271',
      60: '#4A587C',
      65: '#2C3245',
      70: '#161D2E',
      75: '#1F1F2C',
      80: '#1C212F',
      90: '#171A26',
      100: '#141521',
    },
    ink: {
      50: '#F4F4F7',
      100: '#EDEDF1',
      200: '#E6E6EC',
      300: '#CBCBD5',
      400: '#8C8C9E',
      500: '#737386',
      600: '#4C4B5A',
      700: '#2A2A30',
      800: '#202024',
      850: '#1C1C20',
      900: '#18181B',
      950: '#09090B',
    },
    black: {
      70: '#251E33',
      90: '#171A26',
    },
    transparent: {
      10: '#f8f6f6ba',
      15: '#ffffff17',
      20: '#aea8a816',
      70: '#0000002e',
      80: '#3f3f4659',
      100: '#000000a7',
    },
    redTransparent: {
      90: '#34171EEB',
    },
    orangeTransparent: {
      10: '#F7C7A64D',
      90: '#4C200280',
    },
    inputGlass: {
      background: 'rgba(255, 255, 255, 0.34)',
      color: '#252038',
      border: 'rgba(255, 255, 255, 0.6)',
      borderHover: 'rgba(255, 255, 255, 0.8)',
      radius: '0.75rem',
      paddingX: '1.125rem',
      paddingY: '0.8125rem',
      blur: '0.75rem',
    },
    appSidebar: {
      background: '#FFFFFF8C',
      border: '#FFFFFF',
      userBackground: '#FFFFFFC7',
      userBorder: '#FFFFFF',
      notifBadgeBackground: '#F04545',
      notifBadgeColor: '#FFFFFF',
    },
    homeInput: {
      sendIcon: '#8C94A6',
    },
    morePanel: {
      shadow: '0 16px 40px -8px rgba(15, 13, 41, 0.17)',
    },
    workspaceCard: {
      statusColor: '#0FB878',
      iconBackground: '{ink.100}',
    },
    vaultFolder: {
      back: '#C9C6D8',
      front: '#EAE8F0',
      backShared: '#D2544F',
      frontShared: '#F0A7A1',
    },
    searchField: {
      background: '#FFFFFF',
      iconColor: '#73738C',
      placeholderColor: '#73738C',
      segmentTrackBackground: '#F2F2F6',
      segmentColor: '#73738C',
      segmentColorActive: '{ink.900}',
      segmentBackgroundActive: '#FFFFFF',
      segmentShadowActive: '0 1px 3px 0 rgba(15, 13, 41, 0.08)',
    },
    segmentedToggle: {
      trackBackground: '#1714290D',
      segmentColor: '{gray.500}',
      segmentColorActive: '#18181B',
      segmentBackgroundActive: '#FFFFFF',
      segmentShadowActive: '0 1px 3px 0 rgba(15, 13, 41, 0.12)',
    },
    dropdownTrigger: {
      color: '#0A0A0A',
      iconColor: '#73738C',
      borderColor: '{gray.200}',
      background: '#FFFFFF',
      backgroundHover: '#F8F8FA',
      backgroundActive: '{ink.100}',
      backgroundActiveHover: '{ink.200}',
      colorActive: '{ink.900}',
    },
    dropdownMenu: {
      background: '#FFFFFF',
      borderColor: '{gray.200}',
      shadow: '0 16px 40px -8px rgba(15, 13, 41, 0.17)',
      itemColor: '#0A0A0A',
      itemColorActive: '{ink.900}',
      itemBackgroundActive: '{ink.100}',
      countColor: '{gray.400}',
      searchBackground: '#1714290B',
      searchPlaceholder: '#C8C8C8',
      dividerColor: '{gray.200}',
      footerActionColor: '{ink.900}',
      footerClearColor: '{gray.500}',
      checkboxBorderColor: '#D4D4D8',
      checkboxBackgroundChecked: '{ink.900}',
      checkColor: '#FFFFFF',
    },
    activeTag: {
      background: '{ink.100}',
      color: '{ink.900}',
    },
    chatWelcome: {
      cardBackground: '#FFFFFF',
      cardBorderColor: '{gray.200}',
      inkIconBackground: '{ink.100}',
      inkIconColor: '{ink.900}',
      amberIconBackground: '#FCEFD9',
      amberIconColor: '#E0A100',
      blueIconBackground: '#E5EDFB',
      blueIconColor: '#3B82F6',
      redIconBackground: '#FDE5E5',
      redIconColor: '#F04545',
    },
    newCard: {
      background: '#FFFFFF6B',
      borderColor: '{ink.300}',
      titleColor: '{ink.900}',
      folderStroke: '#B6B3C6',
      plusStroke: '#8B87A0',
      labelColor: '#73738C',
    },
    splitterCollapsed: {
      background: 'rgba(255, 255, 255, 0.7)',
      borderColor: '#FFFFFF',
    },
    chatCollapsedInfo: {
      background: 'rgba(255, 255, 255, 0.96)',
      backgroundHover: '#FFFFFF',
      borderColor: 'rgba(89, 89, 115, 0.12)',
      borderColorHover: 'rgba(89, 89, 115, 0.22)',
      color: '#6B6B6B',
      colorHover: '#2B2838',
      shadow: '0 3px 10px -2px rgba(15, 13, 41, 0.1)',
      shadowHover: '0 6px 14px -2px rgba(15, 13, 41, 0.16)',
    },
    fileTypeBar: {
      pdf: '#E5484D',
      sheet: '#12A150',
      doc: '#3B82F6',
      slide: '#E0A100',
      default: '#9C9C9C',
    },
  },
  semantic: {
    primary: {
      50: '{ink.50}',
      100: '{ink.100}',
      200: '{ink.200}',
      300: '{ink.300}',
      400: '{ink.400}',
      500: '{ink.500}',
      600: '{ink.600}',
      700: '{ink.700}',
      800: '{ink.800}',
      900: '{ink.900}',
      950: '{ink.950}',
    },
    formField: {
      borderRadius: 'var(--radius-control)',
    },
    content: {
      borderRadius: 'var(--radius-panel)',
    },
    list: {
      option: {
        borderRadius: 'var(--radius-control)',
      },
    },
    overlay: {
      select: {
        borderRadius: 'var(--radius-panel)',
        shadow: 'var(--overlay-shadow)',
      },
      popover: {
        borderRadius: 'var(--radius-panel)',
        shadow: 'var(--overlay-shadow)',
      },
      modal: {
        borderRadius: 'var(--radius-sheet)',
      },
      navigation: {
        shadow: 'var(--overlay-shadow)',
      },
    },
    colorScheme: {
      light: {
        primary: {
          color: '{ink.900}',
          contrastColor: '#FFFFFF',
          hoverColor: '{ink.700}',
          activeColor: '{ink.950}',
        },
        highlight: {
          background: '{ink.100}',
          focusBackground: '{ink.200}',
          color: '{ink.900}',
          focusColor: '{ink.900}',
        },
        mask: {
          background: 'var(--scrim)',
        },
        overlay: {
          select: {
            background: 'var(--surface-paper)',
            borderColor: 'var(--edge)',
          },
          popover: {
            background: 'var(--surface-paper)',
            borderColor: 'var(--edge)',
          },
          modal: {
            background: 'var(--surface-paper)',
            borderColor: 'var(--edge)',
          },
        },
        list: {
          option: {
            focusBackground: 'var(--row-hover)',
            selectedBackground: 'transparent',
            selectedFocusBackground: 'var(--row-hover)',
            selectedColor: '{text.color}',
            selectedFocusColor: '{text.color}',
          },
        },
        navigation: {
          item: {
            focusBackground: 'var(--row-hover)',
          },
        },
      },
    },
  },
  components: {
    inputtext: {
      colorScheme: {
        light: {
          background: '{inputGlass.background}',
          color: '{inputGlass.color}',
          subtitle: {
            color: '{surface.500}',
          },
          border: {
            color: '{inputGlass.border}',
          },
          hover: {
            border: {
              color: '{inputGlass.borderHover}',
            },
          },
          focus: {
            border: {
              color: '{ink.900}',
            },
          },
          invalid: {
            border: {
              color: '{red.50}',
            },
          },
        },
        dark: {
          background: '{inputGlass.background}',
          color: '{inputGlass.color}',
          subtitle: {
            color: '{whiteGrey.70}',
          },
          border: {
            color: '{inputGlass.border}',
          },
          hover: {
            border: {
              color: '{inputGlass.borderHover}',
            },
          },
          focus: {
            border: {
              color: '{ink.900}',
            },
          },
          invalid: {
            border: {
              color: '{red.60}',
            },
          },
        },
        root: {
          shadow: 'none',
          padding: {
            x: '{inputGlass.paddingX}',
            y: '{inputGlass.paddingY}',
          },
          border: {
            radius: '{inputGlass.radius}',
          },
        },
      },
    },
    autocomplete: {
      borderColor: '{gray.200}',
      hoverBorderColor: '{gray.400}',
      focusBorderColor: '{ink.900}',
      invalidBorderColor: '{red.50}',
      borderRadius: '{inputGlass.radius}',
      focusRing: {
        width: '0',
        shadow: 'none',
      },
    },
    password: {
      colorScheme: {
        light: {
          icon: {
            color: '{whiteGrey.70}',
          },
        },
        dark: {
          icon: {
            color: '{whiteGrey.70}',
          },
        },
      },
    },
    button: {
      root: {
        borderRadius: '0.75rem',
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: 'var(--surface-ink)',
              borderColor: 'transparent',
              color: '#FFFFFF',
              hover: {
                background: 'var(--surface-ink-hover)',
                borderColor: 'transparent',
                color: '#FFFFFF',
              },
              active: {
                background: 'var(--surface-ink-hover)',
                borderColor: 'transparent',
                color: '#FFFFFF',
              },
              focus: {
                ring: {
                  color: '{ink.900}',
                },
              },
            },
            secondary: {
              background: '{whiteGrey.10}',
              color: '{whiteGrey.85}',
              border: {
                color: '{ink.400}',
              },
              hover: {
                background: '{ink.100}',
                color: '{whiteGrey.85}',
                border: {
                  color: '{ink.400}',
                },
              },
              focus: {
                ring: {
                  color: '{ink.900}',
                },
              },
            },
          },
        },
        dark: {
          root: {
            primary: {
              background: 'transparent',
              color: '{whiteGrey.10}',
              hover: {
                background: 'transparent',
                color: '{whiteGrey.10}',
              },
              active: {
                background: 'transparent',
                color: '{whiteGrey.10}',
              },
              focus: {
                ring: {
                  color: '{ink.900}',
                },
              },
            },
            secondary: {
              background: 'transparent',
              color: '{whiteGrey.10}',
              border: {
                color: '{ink.900}',
              },
              hover: {
                background: 'rgba(137,133,148,0.35)',
                color: '{whiteGrey.10}',
              },
              focus: {
                ring: {
                  color: '{ink.900}',
                },
              },
            },
          },
        },
      },
    },
    checkbox: {
      colorScheme: {
        light: {
          background: 'transparent',
          border: {
            color: '{ink.400}',
          },
          hover: {
            border: {
              color: '{ink.600}',
            },
          },
          checked: {
            background: '{ink.900}',
            border: {
              color: '{ink.900}',
            },
            hover: {
              background: '{ink.900}',
              border: {
                color: '{ink.900}',
              },
            },
            focus: {
              border: {
                color: 'transparent',
              },
            },
          },
          icon: {
            checked: {
              color: '{whiteGrey.10}',
              hover: {
                color: '{whiteGrey.10}',
              },
            },
          },
          focus: {
            ring: {
              color: '{ink.900}',
            },
          },
        },
        dark: {
          background: 'transparent',
          border: {
            color: '{ink.900}',
          },
          hover: {
            border: {
              color: '{ink.900}',
            },
          },
          checked: {
            background: '{ink.900}',
            border: {
              color: '{ink.900}',
            },
            hover: {
              background: '{ink.900}',
              border: {
                color: '{ink.900}',
              },
            },
            focus: {
              border: {
                color: 'transparent',
              },
            },
          },
          icon: {
            checked: {
              color: '{whiteGrey.10}',
              hover: {
                color: '{whiteGrey.10}',
              },
            },
          },
          focus: {
            ring: {
              color: '{ink.900}',
            },
          },
        },
      },
    },
    radiobutton: {
      root: {
        borderColor: '{ink.400}',
        hoverBorderColor: '{ink.600}',
      },
    },
    toggleswitch: {
      colorScheme: {
        light: {
          root: {
            background: '#DCDCE3',
            hover: {
              background: '#DCDCE3',
            },
            handle: {
              background: '{whiteGrey.10} !important',
              hover: {
                background: '{whiteGrey.10} !important',
              },
              checked: {
                background: '{whiteGrey.10} !important',
                hover: {
                  background: '{whiteGrey.10} !important',
                },
              },
            },
            checked: {
              background: '{ink.900} !important',
              hover: {
                background: '{ink.900} !important',
              },
            },
            focus: {
              ring: {
                color: '{ink.900}',
              },
            },
          },
        },
        dark: {
          root: {
            background: '{blue.65}',
            handle: {
              background: '{whiteGrey.10} !important',
              hover: {
                background: '{whiteGrey.10} !important',
              },
              checked: {
                background: '{whiteGrey.10} !important',
                hover: {
                  background: '{whiteGrey.10} !important',
                },
              },
            },
            checked: {
              background: '{ink.900} !important',
              hover: {
                background: '{ink.900} !important',
              },
            },
            focus: {
              ring: {
                color: '{ink.900}',
              },
            },
          },
        },
        root: {
          handle: {
            size: '0.875rem',
          },
        },
      },
    },
    multiselect: {
      colorScheme: {
        light: {
          root: {
            background: '{whiteGrey.10}',
            placeholder: {
              color: '{whiteGrey.100}',
            },
            dropdown: {
              color: '{whiteGrey.100}',
            },
            overlay: {
              background: 'var(--surface-paper)',
              border: {
                color: 'var(--edge)',
              },
            },
            option: {
              focus: {
                background: 'var(--row-hover)',
                color: '{whiteGrey.100}',
              },
              selected: {
                background: 'transparent',
                color: '{whiteGrey.100}',
                focus: {
                  background: 'var(--row-hover)',
                  color: '{whiteGrey.100}',
                },
              },
            },
          },
        },
        dark: {
          root: {
            background: '{blue.70}',
            placeholder: {
              color: '{whiteGrey.10}',
            },
            dropdown: {
              color: '{whiteGrey.10}',
            },
            overlay: {
              background: '{blue.70}',
              border: {
                color: '{blue.80}',
              },
            },
            option: {
              focus: {
                background: '{blue.65}',
                color: '{whiteGrey.10}',
              },
              selected: {
                background: '{blue.65}',
                color: '{whiteGrey.10}',
                focus: {
                  background: '{blue.65}',
                  color: '{whiteGrey.10}',
                },
              },
            },
          },
        },
      },
    },
    datepicker: {
      colorScheme: {
        light: {
          root: {
            panel: {
              background: '{whiteGrey.10}',
            },
            header: {
              background: '{whiteGrey.10}',
            },
            input: {
              icon: {
                color: '{whiteGrey.100}',
              },
            },
            date: {
              hover: {
                background: '{ink.100}',
              },
              selected: {
                background: '{ink.900}',
                color: '{whiteGrey.10}',
              },
              range: {
                selected: {
                  background: '{ink.100}',
                  color: '#60606382',
                },
              },
            },
          },
        },
        dark: {
          root: {
            panel: {
              background: '{blue.70}',
            },
            header: {
              background: '{blue.70}',
            },
            input: {
              icon: {
                color: '{whiteGrey.10}',
              },
            },
            date: {
              hover: {
                background: '{blue.55}',
              },
              selected: {
                background: '{blue.55}',
                color: '{whiteGrey.10}',
              },
              range: {
                selected: {
                  background: '#3F3F4669',
                  color: '#FFFFFF54',
                },
              },
            },
          },
        },
      },
    },
    stepper: {
      colorScheme: {
        light: {
          root: {
            separator: {
              background: '{whiteGrey.20}',
              active: {
                background: '{whiteGrey.20}',
              },
            },
            step: {
              title: {
                color: '{whiteGrey.50}',
                font: {
                  weight: '400',
                },
                active: {
                  color: '{whiteGrey.100}',
                },
              },
              header: {
                focus: {
                  ring: {
                    color: '{ink.900}',
                  },
                },
              },
              number: {
                font: {
                  size: '0.875rem',
                },
                border: {
                  color: 'transparent',
                },
                background: '{ink.300}',
                color: '{whiteGrey.20}',
                active: {
                  color: '{whiteGrey.10}',
                  background: '{ink.900}',
                  font: {
                    size: '0.875rem',
                  },
                  border: {
                    color: 'transparent',
                  },
                },
              },
            },
          },
        },
        dark: {
          root: {
            separator: {
              background: '{ink.800}',
              active: {
                background: '{ink.800}',
              },
            },
            step: {
              title: {
                color: '{whiteGrey.60}',
                font: {
                  weight: '400',
                },
                active: {
                  color: '{whiteGrey.10}',
                },
              },
              header: {
                focus: {
                  ring: {
                    color: '{ink.900}',
                  },
                },
              },
              number: {
                font: {
                  size: '0.875rem',
                },
                border: {
                  color: 'transparent',
                },
                background: '{ink.900}',
                active: {
                  color: '{whiteGrey.50}',
                  background: '{ink.900}',
                  font: {
                    size: '0.875rem',
                  },
                  border: {
                    color: 'transparent',
                  },
                },
              },
            },
          },
        },
        root: {
          separator: {
            size: '1px',
          },
        },
      },
    },
    tieredmenu: {
      colorScheme: {
        light: {
          background: 'var(--surface-paper)',
          border: {
            color: 'var(--edge)',
          },
          color: '{whiteGrey.100}',
          item: {
            icon: {
              color: '{whiteGrey.70}',
              focus: {
                color: '{whiteGrey.70}',
              },
            },
            focus: {
              background: 'var(--row-hover)',
            },
          },
        },
        dark: {
          background: '{ink.850}',
          border: {
            color: 'transparent',
          },
          color: '{whiteGrey.10}',
          item: {
            icon: {
              color: '{whiteGrey.10}',
              focus: {
                color: '{whiteGrey.10}',
              },
            },
            focus: {
              background: '{ink.800}',
            },
          },
        },
        root: {
          item: {
            gap: '1rem',
            padding: '0.5rem 1rem',
          },
        },
      },
    },
    menu: {
      colorScheme: {
        light: {
          background: 'var(--surface-paper)',
          border: {
            color: 'var(--edge)',
          },
          color: '{whiteGrey.100}',
          item: {
            icon: {
              color: '{whiteGrey.70}',
              focus: {
                color: '{whiteGrey.70}',
              },
            },
            focus: {
              background: 'var(--row-hover)',
            },
          },
        },
        dark: {
          background: '{ink.850}',
          border: {
            color: 'transparent',
          },
          color: '{whiteGrey.10}',
          item: {
            icon: {
              color: '{whiteGrey.10}',
              focus: {
                color: '{whiteGrey.10}',
              },
            },
            focus: {
              background: '{ink.800}',
            },
          },
        },
        root: {
          item: {
            gap: '1rem',
            padding: '0.5rem 1rem',
          },
        },
      },
    },
    datatable: {
      colorScheme: {
        light: {
          header: {
            cell: {
              background: '{ink.200}',
              hover: {
                background: '{black.70}',
              },
              selected: {
                background: '{black.70}',
              },
              focus: {
                ring: {
                  color: '{ink.900}',
                },
              },
            },
          },
          row: {
            background: 'transparent',
          },
          body: {
            cell: {
              border: {
                color: '{whiteGrey.20}',
              },
            },
          },
          paginator: {
            top: {
              border: {
                color: '{black.70}',
                width: '1px',
              },
            },
          },
        },
        dark: {
          header: {
            cell: {
              background: '{black.90}',
              hover: {
                background: '{black.70}',
              },
              selected: {
                background: '{black.70}',
              },
              focus: {
                ring: {
                  color: '{ink.900}',
                },
              },
            },
          },
          row: {
            background: 'transparent',
          },
          body: {
            cell: {
              border: {
                color: '{ink.800}',
              },
            },
          },
          paginator: {
            top: {
              border: {
                color: '{black.70}',
                width: '1px',
              },
            },
          },
        },
      },
      headerCell: {
        sm: {
          padding: '{datatable.header.cell.padding}',
        },
      },
      bodyCell: {
        sm: {
          padding: '{datatable.body.cell.padding}',
        },
      },
      footerCell: {
        sm: {
          padding: '{datatable.footer.cell.padding}',
        },
      },
    },
    paginator: {
      colorScheme: {
        light: {},
        dark: {
          background: 'transparent',
          nav: {
            button: {
              focus: {
                ring: {
                  color: '{ink.900}',
                },
              },
            },
          },
        },
      },
    },
    dialog: {
      colorScheme: {
        light: {
          background: 'var(--surface-paper)',
          border: {
            color: 'var(--edge)',
          },
        },
        dark: {
          background: 'linear-gradient(218.96deg, #161A28 0%, #13111B 100%);',
          border: {
            color: '{ink.800}',
          },
        },
        root: {
          header: {
            padding: '2rem 2.5rem 0 2.5rem',
          },
          title: {
            font: {
              weight: '500',
            },
          },
          content: {
            padding: '0 2.5rem 2rem 2.5rem',
          },
        },
      },
    },
    select: {
      colorScheme: {
        light: {
          background: '{whiteGrey.10}',
          shadow: '0px 0px 7.7px 0px #ABABAB1A',
          color: '{whiteGrey.100}',
          border: {
            color: '{whiteGrey.20}',
          },
          disabled: {
            background: '{transparent.20}',
          },
          focus: {
            border: {
              color: '{whiteGrey.20}',
            },
          },
          hover: {
            border: {
              color: '{whiteGrey.20}',
            },
          },
          placeholder: {
            color: '{whiteGrey.40}',
          },
          dropdown: {
            color: '{whiteGrey.100}',
          },
          overlay: {
            background: 'var(--surface-paper)',
            border: {
              color: 'var(--edge)',
            },
          },
          option: {
            focus: {
              background: 'var(--row-hover)',
              color: '{whiteGrey.100}',
            },
            selected: {
              background: 'transparent',
              color: '{whiteGrey.100}',
              focus: {
                background: 'var(--row-hover)',
                color: '{whiteGrey.100}',
              },
            },
          },
        },
        dark: {
          background: '{blue.70}',
          color: '{whiteGrey.10}',
          border: {
            color: '{blue.80}',
          },
          disabled: {
            background: '{transparent.80}',
          },
          focus: {
            border: {
              color: '{ink.800}',
            },
          },
          hover: {
            border: {
              color: 'transparent',
            },
          },
          placeholder: {
            color: '{whiteGrey.10}',
          },
          dropdown: {
            color: '{whiteGrey.10}',
          },
          overlay: {
            background: '{blue.70}',
            border: {
              color: '{blue.80}',
            },
          },
          option: {
            focus: {
              background: '{blue.65}',
              color: '{whiteGrey.10}',
            },
            selected: {
              background: '{blue.65}',
              color: '{whiteGrey.10}',
              focus: {
                background: '{blue.65}',
                color: '{whiteGrey.10}',
              },
            },
          },
        },
        root: {
          shadow: 'unset',
        },
      },
    },
    drawer: {
      colorScheme: {
        light: {
          background: 'var(--surface-paper)',
          border: {
            color: 'var(--edge)',
          },
        },
        dark: {
          background: '{ink.950}',
        },
      },
      content: {
        padding: 0,
      },
    },
    divider: {
      colorScheme: {
        light: {},
        dark: {
          border: {
            color: '{ink.800}',
          },
        },
      },
    },
    tabs: {
      colorScheme: {
        light: {
          active: {
            bar: {
              background: '{ink.900}',
            },
          },
          tablist: {
            background: 'transparent',
            border: {
              color: '{gray.200}',
            },
          },
          tab: {
            color: '{gray.400}',
            border: {
              color: 'transparent',
            },
            focus: {
              ring: {
                color: '{ink.900}',
              },
            },
            active: {
              color: '{gray.950}',
              border: {
                color: 'transparent',
              },
            },
            hover: {
              color: '{gray.950}',
            },
          },
          tabpanel: {
            background: 'transparent',
          },
        },
        dark: {
          active: {
            bar: {
              background: '{ink.600}',
            },
          },
          tablist: {
            background: 'transparent',
            border: {
              color: '#FFFFFF14',
            },
          },
          tab: {
            color: '{whiteGrey-10}',
            border: {
              color: '#FFFFFF14',
            },
            focus: {
              ring: {
                color: '{ink.900}',
              },
            },
            active: {
              color: '{whiteGrey-10}',
              border: {
                color: '{ink.600}',
              },
            },
            hover: {
              color: '{whiteGrey-10}',
            },
          },
          tabpanel: {
            background: 'transparent',
          },
        },
      },
    },
    timeline: {
      colorScheme: {
        light: {},
        dark: {
          event: {
            connector: {
              color: '{whiteGrey.80}',
            },
          },
        },
      },
    },
    textarea: {
      colorScheme: {
        light: {
          background: '{whiteGrey.15}',
          placeholder: {
            color: '{whiteGrey.50}',
          },
          border: {
            color: '{whiteGrey.30}',
          },
          hover: {
            border: {
              color: '{whiteGrey.30}',
            },
          },
          focus: {
            border: {
              color: '{blue.6}',
            },
          },
          invalid: {
            border: {
              color: '{red.50}',
            },
          },
        },
        dark: {
          background: '{blue.80}',
          border: {
            color: '{blue.80}',
          },
          hover: {
            border: {
              color: '{blue.80}',
            },
          },
          focus: {
            border: {
              color: '{blue.65}',
            },
          },
          invalid: {
            border: {
              color: '{red.60}',
            },
          },
        },
      },
    },
    progressbar: {
      colorScheme: {
        light: {
          background: '{whiteGrey.20}',
          value: {
            background: '{ink.900}',
          },
        },
        dark: {
          background: '{blue.80}',
          value: {
            background: '{ink.900}',
          },
        },
      },
    },
    slider: {
      colorScheme: {
        light: {
          handle: {
            content: {
              background: 'transparent',
            },
            focus: {
              ring: {
                color: '{ink.800}',
              },
            },
          },
        },
        dark: {
          handle: {
            content: {
              background: 'transparent',
            },
            focus: {
              ring: {
                color: '{ink.800}',
              },
            },
          },
        },
      },
    },
    panel: {
      colorScheme: {
        light: {
          background: 'transparent',
          border: {
            color: 'transparent',
          },
        },
        dark: {
          background: 'transparent',
          border: {
            color: 'transparent',
          },
        },
      },
    },
    confirmpopup: {
      colorScheme: {
        light: {},
        dark: {
          background: 'linear-gradient(218.96deg, #161A28 0%, #13111B 100%)',
          border: {
            color: '{ink.800}',
          },
        },
      },
    },
    tooltip: {
      colorScheme: {
        light: {
          background: '{ink.200}',
          color: '{whiteGrey.100}',
        },
        dark: {
          background: '{blue.65}',
          color: '{whiteGrey.10}',
        },
      },
    },
    skeleton: {
      colorScheme: {
        light: {
          root: {
            background: '#ececf1',
          },
        },
      },
    },
    popover: {
      colorScheme: {
        light: {
          background: 'var(--surface-paper)',
          border: {
            color: 'var(--edge)',
          },
        },
        dark: {
          background: '{ink.850}',
          border: {
            color: '{ink.800}',
          },
        },
      },
    },
    chip: {
      colorScheme: {
        light: {
          background: '{whiteGrey.20}',
          border: {
            color: '{whiteGrey.20}',
          },
        },
        dark: {
          background: '#1E1C36',
          border: {
            color: '{ink.800}',
          },
        },
      },
    },
    toast: {
      colorScheme: {
        light: {
          error: {
            background: '#ffb6bf',
          },
          success: {
            background: '{green.20}',
            border: {
              color: '{green.20}',
            },
            close: {
              button: {
                hover: {
                  background: 'transparent',
                },
              },
            },
          },
        },
      },
    },
    splitter: {
      colorScheme: {
        light: {
          gutter: {
            background: '{whiteGrey.40}',
          },
          handle: {
            background: '{whiteGrey.60}',
          },
        },
        dark: {
          gutter: {
            background: '{ink.850}',
          },
          handle: {
            background: '{grey.20}',
          },
        },
      },
    },
  },
  extend: {
    formFieldBackgroundColorDark: '{blue.80}',
  },
} as unknown as Preset);
